import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sectionTargetId, targetKey } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtCountdown, fmtTime, useNow } from '../lib/format';
import { Button, Card, cx, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';
import { DiscussLive } from './Rooms';
import { CommentThread, DiscussToggle, EntityMenu, FollowButton, ReactionBar, ReadReceipts, useSocialSummary } from '../components/social';

interface Section {
  id: string;
  instrument: { id: string; symbol: string; displayName: string };
  complete: boolean;
  noView: boolean;
  noViewReason: string | null;
  bias: string | null;
  confidence: string | null;
  htfContext: string | null;
  keyLevels: { label: string; price: string; kind: string }[];
  marketProfile: Record<string, string> | null;
  scenarios: { when: string; outcome: string }[];
  events: string | null;
  notes: string | null;
  /** INT-ANL-04: false for "No view" sections */
  reviewable?: boolean;
  review?: SectionReview | null;
}
type Verdict = 'YES' | 'PARTLY' | 'NO';
interface SectionReview {
  biasCorrect: Verdict;
  levelsRespected: Verdict | null;
  notes: string | null;
  reviewedAt: string;
}
interface AnalysisResp {
  id: string;
  author: { id: string; name: string };
  market: { id: string; code: string; name: string };
  tradingDay: string;
  status: string;
  deadlineAt: string;
  publishedAt: string | null;
  isLate: boolean;
  version: number;
  progress: { done: number; total: number };
  sections: Section[];
  review?: { opensAt: string | null; open: boolean; canEdit: boolean; reviewed: number; total: number };
  /** D25: the author can change the analysis only until its trading day is over */
  editable?: boolean;
  editableUntil?: string;
}

const VERDICTS: { value: Verdict; label: string }[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'PARTLY', label: 'Partly' },
  { value: 'NO', label: 'No' },
];
const VERDICT_COLORS: Record<Verdict, string> = { YES: '#3dd68c', PARTLY: '#f5b544', NO: '#ff7a6e' };
const VERDICT_TONE: Record<Verdict, 'up' | 'warn' | 'down'> = { YES: 'up', PARTLY: 'warn', NO: 'down' };
const BIAS_WORD: Record<Verdict, string> = { YES: 'Bias right', PARTLY: 'Bias partly right', NO: 'Bias wrong' };
const LEVELS_WORD: Record<Verdict, string> = { YES: 'Levels respected', PARTLY: 'Levels partly respected', NO: 'Levels not respected' };

/** The end-of-day review as everyone sees it on a section (INT-ANL-04). */
function ReviewSummary({ r }: { r: SectionReview }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line2 bg-raised px-3 py-2 text-sm">
      <span className="text-xs tracking-[0.06em] text-muted uppercase">End-of-day review</span>
      <Pill tone={VERDICT_TONE[r.biasCorrect]}>{BIAS_WORD[r.biasCorrect]}</Pill>
      {r.levelsRespected && <Pill tone={VERDICT_TONE[r.levelsRespected]}>{LEVELS_WORD[r.levelsRespected]}</Pill>}
      {r.notes && <span className="w-full text-ink2">{r.notes}</span>}
    </div>
  );
}

type ReviewDraft = Record<string, { biasCorrect: Verdict | null; levelsRespected: Verdict | null; notes: string }>;

/** INT-ANL-04: the author marks how each symbol played out once the trading day is over. Editable later (audited). */
function ReviewForm({ a, onSaved }: { a: AnalysisResp; onSaved: (a: AnalysisResp) => void }) {
  const sections = a.sections.filter((s) => s.reviewable);
  const [draft, setDraft] = useState<ReviewDraft>(() => Object.fromEntries(sections.map((s) => [s.instrument.id, { biasCorrect: s.review?.biasCorrect ?? null, levelsRespected: s.review?.levelsRespected ?? null, notes: s.review?.notes ?? '' }])));
  const [dirty, setDirty] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api<AnalysisResp>(`/analyses/${a.id}/review`, {
        method: 'PUT',
        body: { sections: sections.filter((s) => draft[s.instrument.id]?.biasCorrect).map((s) => ({ instrumentId: s.instrument.id, biasCorrect: draft[s.instrument.id]!.biasCorrect, levelsRespected: draft[s.instrument.id]!.levelsRespected, notes: draft[s.instrument.id]!.notes.trim() || null })) },
      }),
    onSuccess: (res) => {
      setDirty(false);
      onSaved(res);
    },
  });
  const edit = (id: string, patch: Partial<ReviewDraft[string]>) => {
    setDraft((d) => ({ ...d, [id]: { ...d[id]!, ...patch } }));
    setDirty(true);
  };
  const answered = sections.filter((s) => draft[s.instrument.id]?.biasCorrect).length;
  return (
    <Card tone="accent" className="flex scroll-mt-4 flex-col">
      <div id="review" className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 className="m-0 text-[15px] font-semibold">
          End-of-day review <span className="font-normal text-muted">· {a.review?.reviewed ?? 0} of {sections.length} symbols reviewed</span>
        </h2>
        <span className="text-xs text-muted">Counts towards your analysis accuracy in Analytics</span>
      </div>
      <div>
      {sections.map((s) => {
        const d = draft[s.instrument.id]!;
        return (
          <div key={s.id} role="group" aria-label={`Review ${s.instrument.symbol}`} className="flex flex-col gap-3 border-t border-[#1a1f27] px-4 py-3.5 first:border-t-0">
            <span className="font-semibold">
              {s.instrument.symbol} <span className="font-normal text-muted">· you said {s.bias?.toLowerCase()}{s.keyLevels.length ? ` · ${s.keyLevels.length} key level${s.keyLevels.length === 1 ? '' : 's'}` : ''}</span>
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">Bias correct?</span>
                <Segmented label={`${s.instrument.symbol}: bias correct?`} options={VERDICTS} value={d.biasCorrect} onChange={(v) => edit(s.instrument.id, { biasCorrect: v })} colors={VERDICT_COLORS} />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">Key levels respected?</span>
                <Segmented label={`${s.instrument.symbol}: key levels respected?`} options={VERDICTS} value={d.levelsRespected} onChange={(v) => edit(s.instrument.id, { levelsRespected: v === d.levelsRespected ? null : v })} colors={VERDICT_COLORS} />
              </div>
            </div>
            <Field label={`Notes on ${s.instrument.symbol} (optional)`}>
              <Input value={d.notes} maxLength={2000} onChange={(e) => edit(s.instrument.id, { notes: e.target.value })} placeholder="What the market did and what you'd do differently" />
            </Field>
          </div>
        );
      })}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-3">
        <Button variant="primary" onClick={() => save.mutate()} disabled={!dirty || !answered || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save review'}
        </Button>
        <span role="status" className="text-sm text-sub">
          {save.isSuccess && !dirty ? 'Review saved.' : !answered ? 'Answer "Bias correct?" for at least one symbol.' : dirty ? 'Unsaved changes' : ''}
        </span>
        <ErrorText error={save.error ? errorText(save.error) : null} />
      </div>
    </Card>
  );
}

const BIAS_OPTS = [
  { value: 'BULLISH', label: '▲ Bullish' },
  { value: 'BEARISH', label: '▼ Bearish' },
  { value: 'NEUTRAL', label: '■ Neutral' },
];
const BIAS_COLORS = { BULLISH: '#3dd68c', BEARISH: '#ff7a6e', NEUTRAL: '#e7ebf0' };
const CONF_OPTS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];
const PROFILE = [
  ['vah', 'Prior VAH'],
  ['val', 'Prior VAL'],
  ['poc', 'Prior POC'],
  ['ibHigh', 'IB high'],
  ['ibLow', 'IB low'],
] as const;

const done = (s: Section) => (s.noView ? !!s.noViewReason?.trim() : !!s.bias);
const toBody = (s: Section) => ({
  instrumentId: s.instrument.id,
  noView: s.noView,
  noViewReason: s.noViewReason,
  bias: s.noView ? null : s.bias,
  confidence: s.confidence,
  htfContext: s.htfContext,
  keyLevels: s.keyLevels.filter((l) => l.price.trim() !== '' && !Number.isNaN(Number(l.price))),
  marketProfile: s.marketProfile ? Object.fromEntries(Object.entries(s.marketProfile).filter(([, v]) => v !== '' && v != null)) : null,
  scenarios: s.scenarios.filter((x) => x.when.trim() || x.outcome.trim()),
  events: s.events,
  notes: s.notes,
});

export function AnalysisEditor() {
  const me = useMe();
  const qc = useQueryClient();
  const now = useNow(1000);
  const [params, setParams] = useSearchParams();
  const tz = me.data?.timezone ?? 'UTC';
  const markets = useMemo(() => {
    const m = new Map<string, { id: string; code: string; name: string }>();
    me.data?.accounts.forEach((a) => m.set(a.market.id, a.market));
    return [...m.values()];
  }, [me.data]);
  const marketId = params.get('market') || markets[0]?.id || '';
  const { data, error: loadErr } = useQuery({ queryKey: ['analysis-today', marketId], queryFn: () => api<AnalysisResp>(`/analyses/today?marketId=${marketId}`), enabled: !!marketId });
  const [draft, setDraft] = useState<Section[]>([]);
  const [current, setCurrent] = useState(0);
  const [dirty, setDirty] = useState(false);
  const loadedId = useRef<string | null>(null);

  useEffect(() => {
    if (data && loadedId.current !== `${data.id}:${data.version}`) {
      loadedId.current = `${data.id}:${data.version}`;
      setDraft(data.sections);
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api<AnalysisResp>(`/analyses/${data!.id}`, { method: 'PUT', body: { sections: draft.map(toBody) } }),
    onSuccess: (a) => {
      setDirty(false);
      qc.setQueryData(['analysis-today', marketId], a);
    },
  });
  const submit = useMutation({
    mutationFn: async () => {
      if (dirty) await api(`/analyses/${data!.id}`, { method: 'PUT', body: { sections: draft.map(toBody) } });
      return api<AnalysisResp>(`/analyses/${data!.id}/submit`, { method: 'POST' });
    },
    onSuccess: (a) => {
      setDirty(false);
      qc.setQueryData(['analysis-today', marketId], a);
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  // autosave drafts 2 s after the last change (published analyses save explicitly: each save is a new version)
  useEffect(() => {
    if (!dirty || !data || data.status !== 'DRAFT' || data.editable === false) return;
    const t = setTimeout(() => save.mutate(), 2000);
    return () => clearTimeout(t);
  }, [draft, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const sec = draft[current];
  const update = (patch: Partial<Section>) => {
    setDraft((d) => d.map((s, i) => (i === current ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  if (!me.data) return null;
  if (!markets.length) return <Card className="p-5 text-sub">You have no accounts yet, so there's no market to analyse. Ask an admin to assign you an account.</Card>;

  const doneCount = draft.filter(done).length;
  const deadlineMs = data ? new Date(data.deadlineAt).getTime() - now : 0;
  const published = data?.status === 'PUBLISHED';
  const locked = data?.editable === false;

  return (
    <>
      <PageHeader
        title={`Daily analysis · ${data?.market.code ?? ''}`}
        sub={
          data
            ? `${data.tradingDay} · ${published ? `Published ${fmtTime(data.publishedAt, tz)} · v${data.version}${data.isLate ? ' · late' : ''}` : locked ? 'Not submitted' : dirty ? 'Unsaved changes' : save.isPending ? 'Saving…' : 'Draft · saved'}${!locked && data.editableUntil ? ` · editable until ${fmtTime(data.editableUntil, tz)}` : ''}`
            : 'Loading…'
        }
        back={<Link to="/" className="text-[13px]">← Today</Link>}
        right={
          <>
            {markets.length > 1 && (
              <Select aria-label="Market" value={marketId} onChange={(e) => setParams({ market: e.target.value })} className="w-auto">
                {markets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code}
                  </option>
                ))}
              </Select>
            )}
            {data && !published && !locked && (
              <div className={cx('flex flex-col items-end rounded-lg border px-3 py-1.5', deadlineMs < 0 ? 'border-[#6a2a27] bg-down-bg' : 'border-[#5a4316] bg-warn-bg')}>
                <span className={cx('text-[11px] font-semibold tracking-[0.06em] uppercase', deadlineMs < 0 ? 'text-down' : 'text-warn')}>Deadline {fmtTime(data.deadlineAt, tz)}</span>
                <span className={cx('font-mono text-base font-semibold', deadlineMs < 0 ? 'text-down' : 'text-warn')}>{fmtCountdown(data.deadlineAt, now)}</span>
              </div>
            )}
            {locked ? (
              published && (
                <Link to={`/analyses/${data!.id}?review=1`} className="font-semibold">
                  Review this analysis →
                </Link>
              )
            ) : published ? (
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                Save as new version
              </Button>
            ) : (
              <Button variant="primary" onClick={() => submit.mutate()} disabled={submit.isPending || !data}>
                {submit.isPending ? 'Publishing…' : 'Submit & publish to floor'}
              </Button>
            )}
          </>
        }
      />
      <ErrorText error={loadErr ? errorText(loadErr) : submit.error ? errorText(submit.error) : save.error ? errorText(save.error) : null} />

      {data && locked && (
        <Card tone="warn" className="p-4 text-sub">
          {published ? (
            <>
              The {data.market.code} trading day for this analysis is over, so it can't be changed any more. Add your end-of-day review or a comment on the{' '}
              <Link to={`/analyses/${data.id}?review=1`}>analysis page</Link>.
            </>
          ) : (
            <>The {data.market.code} trading day is over and this analysis wasn't submitted, so it can't be published any more.</>
          )}
        </Card>
      )}

      {data && !draft.length && <Card className="p-5 text-sub">No symbols in {data.market.code} are assigned to you yet.</Card>}

      {sec && (
        <div className="flex flex-wrap items-start gap-4">
          <aside aria-label="Assigned symbols" className="flex w-full flex-col gap-2 md:w-[240px]">
            <div className="flex justify-between text-xs tracking-[0.06em] text-muted uppercase">
              <span>Assigned symbols</span>
              <span>
                {doneCount} / {draft.length} done
              </span>
            </div>
            <div className="h-1.5 rounded bg-[#232a34]">
              <div className="h-1.5 rounded bg-up" style={{ width: `${(doneCount / draft.length) * 100}%` }} />
            </div>
            {draft.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-current={i === current}
                onClick={() => setCurrent(i)}
                className={cx('flex cursor-pointer items-center justify-between rounded-lg border p-3 text-left text-ink', i === current ? 'border-[#2b4a78] bg-accent-bg' : 'border-line bg-panel')}
              >
                <span className="font-semibold">{s.instrument.symbol}</span>
                <span className={cx('text-xs', done(s) ? 'text-up' : i === current ? 'text-accent' : 'text-warn')}>{done(s) ? (s.noView ? 'No view · done' : `${(s.bias ?? '').toLowerCase()} · done`) : i === current ? 'Writing' : 'To do'}</span>
              </button>
            ))}
          </aside>

          <fieldset disabled={locked} className="m-0 flex min-w-0 flex-[999_1_520px] flex-col gap-3.5 border-0 p-0">
            <Card className="flex flex-col gap-3.5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="m-0 text-lg font-bold">
                  {sec.instrument.symbol} <span className="text-sm font-normal text-muted">· {sec.instrument.displayName}</span>
                </h2>
                <label className="flex cursor-pointer items-center gap-2 text-sub">
                  <input type="checkbox" className="h-[18px] w-[18px]" checked={sec.noView} onChange={(e) => update({ noView: e.target.checked })} /> No view today
                </label>
              </div>
              {sec.noView ? (
                <Field label="Why no view? (counts as done)">
                  <Input value={sec.noViewReason ?? ''} onChange={(e) => update({ noViewReason: e.target.value })} placeholder="e.g. Results day, no edge" />
                </Field>
              ) : (
                <div className="flex flex-wrap gap-5">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-muted">Bias</span>
                    <Segmented label="Bias" options={BIAS_OPTS} value={sec.bias} onChange={(v) => update({ bias: v })} colors={BIAS_COLORS} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-muted">Confidence</span>
                    <Segmented label="Confidence" options={CONF_OPTS} value={sec.confidence} onChange={(v) => update({ confidence: v })} />
                  </div>
                </div>
              )}
              <Field label="Higher-timeframe context">
                <Textarea value={sec.htfContext ?? ''} onChange={(e) => update({ htfContext: e.target.value })} />
              </Field>
            </Card>

            <Card className="flex flex-col gap-2.5 p-4">
              <div className="flex items-center justify-between">
                <h3 className="m-0 text-[15px] font-semibold">Key levels</h3>
                <Button variant="ghost" onClick={() => update({ keyLevels: [...sec.keyLevels, { label: '', price: '', kind: 'Support' }] })}>
                  + Add level
                </Button>
              </div>
              {sec.keyLevels.map((l, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <Input aria-label="Level name" placeholder="Name" value={l.label} onChange={(e) => update({ keyLevels: sec.keyLevels.map((x, j) => (j === idx ? { ...x, label: e.target.value } : x)) })} />
                  <Input aria-label="Price" placeholder="Price" mono inputMode="decimal" value={l.price} onChange={(e) => update({ keyLevels: sec.keyLevels.map((x, j) => (j === idx ? { ...x, price: e.target.value } : x)) })} />
                  <Select aria-label="Kind" value={l.kind} onChange={(e) => update({ keyLevels: sec.keyLevels.map((x, j) => (j === idx ? { ...x, kind: e.target.value } : x)) })}>
                    {['Support', 'Resistance', 'PDH', 'PDL', 'PDC', 'Weekly', 'Other'].map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </Select>
                  <Button variant="ghost" aria-label="Remove level" onClick={() => update({ keyLevels: sec.keyLevels.filter((_, j) => j !== idx) })}>
                    ✕
                  </Button>
                </div>
              ))}
            </Card>

            <details className="rounded-xl border border-line bg-panel p-4">
              <summary className="cursor-pointer text-[15px] font-semibold">
                Market profile <span className="font-normal text-muted">· optional</span>
              </summary>
              <div className="mt-3 grid grid-cols-2 gap-2.5 md:grid-cols-5">
                {PROFILE.map(([k, l]) => (
                  <Field key={k} label={l}>
                    <Input mono inputMode="decimal" value={sec.marketProfile?.[k] ?? ''} onChange={(e) => update({ marketProfile: { ...(sec.marketProfile ?? {}), [k]: e.target.value } })} />
                  </Field>
                ))}
              </div>
              <div className="mt-2.5 grid gap-2.5 md:grid-cols-2">
                <Field label="Expected opening type">
                  <Select value={sec.marketProfile?.openingType ?? ''} onChange={(e) => update({ marketProfile: { ...(sec.marketProfile ?? {}), openingType: e.target.value } })}>
                    <option value="">—</option>
                    <option value="OPEN_DRIVE">Open drive</option>
                    <option value="OPEN_TEST_DRIVE">Open test drive</option>
                    <option value="OPEN_REJECTION_REVERSE">Open rejection reverse</option>
                    <option value="OPEN_AUCTION">Open auction</option>
                  </Select>
                </Field>
                <Field label="Expected day type">
                  <Select value={sec.marketProfile?.dayType ?? ''} onChange={(e) => update({ marketProfile: { ...(sec.marketProfile ?? {}), dayType: e.target.value } })}>
                    <option value="">—</option>
                    <option value="NORMAL">Normal</option>
                    <option value="NORMAL_VARIATION">Normal variation</option>
                    <option value="TREND">Trend</option>
                    <option value="DOUBLE_DISTRIBUTION">Double distribution</option>
                    <option value="NEUTRAL">Neutral</option>
                  </Select>
                </Field>
              </div>
            </details>

            <Card className="flex flex-col gap-2.5 p-4">
              <div className="flex items-center justify-between">
                <h3 className="m-0 text-[15px] font-semibold">Scenarios</h3>
                <Button variant="ghost" onClick={() => update({ scenarios: [...sec.scenarios, { when: '', outcome: '' }] })}>
                  + Add scenario
                </Button>
              </div>
              {sec.scenarios.map((s, idx) => (
                <div key={idx} className="grid gap-2 md:grid-cols-2">
                  <Field label="If">
                    <Input value={s.when} onChange={(e) => update({ scenarios: sec.scenarios.map((x, j) => (j === idx ? { ...x, when: e.target.value } : x)) })} />
                  </Field>
                  <Field label="Then">
                    <Input value={s.outcome} onChange={(e) => update({ scenarios: sec.scenarios.map((x, j) => (j === idx ? { ...x, outcome: e.target.value } : x)) })} />
                  </Field>
                </div>
              ))}
            </Card>

            <div className="grid gap-3.5 md:grid-cols-2">
              <Card className="p-4">
                <Field label="Events to watch">
                  <Input value={sec.events ?? ''} onChange={(e) => update({ events: e.target.value })} />
                </Field>
              </Card>
              <Card className="p-4">
                <Field label="Notes">
                  <Input value={sec.notes ?? ''} onChange={(e) => update({ notes: e.target.value })} />
                </Field>
              </Card>
            </div>
          </fieldset>
        </div>
      )}
    </>
  );
}

export function AnalysisView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ['analysis', id], queryFn: () => api<AnalysisResp>(`/analyses/${id}`) });
  const focusReview = params.get('review') === '1';
  const focusSection = params.get('section');
  const focusComment = params.get('comment');
  const [open, setOpen] = useState<Set<string>>(() => new Set(focusSection ? [focusSection] : []));
  const isAuthor = !!data && data.author.id === me.data?.id;
  const published = data?.status === 'PUBLISHED';
  const keys = data ? [targetKey('ANALYSIS', data.id), ...data.sections.map((s) => targetKey('ANALYSIS', sectionTargetId(data.id, s.instrument.id)))] : [];
  const summary = useSocialSummary(keys, published);
  const read = useMutation({ mutationFn: (analysisId: string) => api('/read-receipts', { body: { targetType: 'ANALYSIS', targetId: analysisId } }) });

  // INT-ANL-03: opening a published analysis records a read receipt (the API ignores the author's own opens)
  useEffect(() => {
    if (data && published && !isAuthor && me.data) read.mutate(data.id);
  }, [data?.id, published, isAuthor, me.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusSection && data && !focusComment) document.getElementById(`sec-${focusSection}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focusSection, focusComment, data?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusReview && data?.review?.canEdit) document.getElementById('review')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focusReview, data?.id, data?.review?.canEdit]);

  if (error) return <ErrorText error={errorText(error)} />;
  if (!data) return null;
  const toggle = (instrumentId: string) => setOpen((o) => (o.has(instrumentId) ? new Set([...o].filter((x) => x !== instrumentId)) : new Set([...o, instrumentId])));
  const whole = summary.data?.[targetKey('ANALYSIS', data.id)];
  const what = `${data.author.name}'s ${data.market.code} analysis`;
  return (
    <>
      <PageHeader
        title={`${data.market.code} analysis · ${data.author.name}`}
        sub={`${data.tradingDay} · published ${fmtTime(data.publishedAt, tz)} · v${data.version}`}
        back={<Link to="/floor" className="text-[13px]">← Floor</Link>}
        right={
          <>
            {published && (isAuthor || hasRole(me.data?.roles, 'MASTER_TRADER')) && <ReadReceipts analysisId={data.id} authorId={data.author.id} />}
            {(isAuthor || hasRole(me.data?.roles, 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN')) && <DiscussLive type="ANALYSIS" id={data.id} />}
            {!isAuthor && <FollowButton targetType="USER" targetId={data.author.id} label={data.author.name} />}
            {data.isLate ? <Pill tone="warn">Late</Pill> : <Pill tone="up">On time</Pill>}
          </>
        }
      />
      {data.review?.canEdit && data.review.total > 0 && (
        <ReviewForm
          a={data}
          onSaved={(a) => {
            qc.setQueryData(['analysis', id], a);
            void qc.invalidateQueries({ queryKey: ['today'] });
            void qc.invalidateQueries({ queryKey: ['analytics'] });
          }}
        />
      )}
      {isAuthor && published && data.review && !data.review.open && data.review.opensAt && data.review.total > 0 && (
        <Card className="p-3.5 text-sub">You can review how this analysis played out once the {data.market.code} trading day is over, from {fmtTime(data.review.opensAt, tz, true)}.</Card>
      )}
      <div className="grid items-start gap-3.5 lg:grid-cols-2">
        {data.sections.map((s) => {
          const secId = sectionTargetId(data.id, s.instrument.id);
          const sum = summary.data?.[targetKey('ANALYSIS', secId)];
          const isOpen = open.has(s.instrument.id);
          const biasWord = s.noView ? 'no view' : `${s.bias?.toLowerCase()}`;
          return (
            <Card key={s.id} className="flex flex-col gap-2.5 p-4">
              <div id={`sec-${s.instrument.id}`} className="flex scroll-mt-4 items-center justify-between gap-2">
                <h2 className="m-0 text-lg font-bold">
                  <EntityMenu type="INSTRUMENT" id={s.instrument.id} label={s.instrument.symbol} />
                </h2>
                <span className={cx('font-semibold', s.bias === 'BULLISH' ? 'text-up' : s.bias === 'BEARISH' ? 'text-down' : 'text-sub')}>{s.noView ? 'No view' : `${s.bias?.toLowerCase()} · ${s.confidence?.toLowerCase() ?? ''}`}</span>
              </div>
              {s.noView && <p className="m-0 text-sub">{s.noViewReason}</p>}
              {s.htfContext && <p className="m-0 leading-relaxed text-ink2">{s.htfContext}</p>}
              {!!s.keyLevels.length && (
                <div className="flex flex-wrap gap-2">
                  {s.keyLevels.map((l, i) => (
                    <span key={i} className="rounded-lg bg-raised px-2.5 py-1 text-[13px]">
                      <span className="text-muted">{l.label || l.kind}</span> <span className="font-mono font-semibold">{l.price}</span>
                    </span>
                  ))}
                </div>
              )}
              {s.marketProfile && !!Object.keys(s.marketProfile).length && (
                <span className="text-xs text-sub">
                  Profile: {Object.entries(s.marketProfile).map(([k, v]) => `${k} ${v}`).join(' · ')}
                </span>
              )}
              {s.scenarios.map((sc, i) => (
                <p key={i} className="m-0 text-sm">
                  <span className="text-muted">If</span> {sc.when} <span className="text-muted">→</span> {sc.outcome}
                </p>
              ))}
              {s.events && <span className="text-xs text-sub">Events: {s.events}</span>}
              {s.notes && <span className="text-sm text-ink2">{s.notes}</span>}
              {s.review && <ReviewSummary r={s.review} />}
              {published && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
                  {!s.noView && s.bias && <ReactionBar targetType="ANALYSIS" targetId={secId} kinds={['AGREE', 'DISAGREE']} summary={sum} readOnly={isAuthor} what={`with ${s.instrument.symbol} ${biasWord}`} />}
                  <DiscussToggle count={sum?.comments ?? 0} open={isOpen} onClick={() => toggle(s.instrument.id)} controls={`thread-${s.instrument.id}`} label={`Discuss ${s.instrument.symbol}`} />
                </div>
              )}
              {isOpen && (
                <div id={`thread-${s.instrument.id}`}>
                  <CommentThread targetType="ANALYSIS" targetId={secId} what={`${s.instrument.symbol} in ${what}`} highlight={focusSection === s.instrument.id ? focusComment : null} autoFocus={!focusSection} />
                </div>
              )}
            </Card>
          );
        })}
      </div>
      {published && (
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="m-0 text-[15px] font-semibold">Discussion · whole analysis</h2>
            <ReactionBar targetType="ANALYSIS" targetId={data.id} kinds={['LIKE']} summary={whole} readOnly={isAuthor} what="this analysis" />
          </div>
          <CommentThread targetType="ANALYSIS" targetId={data.id} what={what} highlight={!focusSection ? focusComment : null} />
        </Card>
      )}
    </>
  );
}
